import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays } from "lucide-react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function HolidayCalendar() {
  const [year, setYear] = useState(new Date().getFullYear());
  const { data: years } = useQuery({ queryKey: ["holidayYears"], queryFn: async () => (await api.get("/holidays/years")).data });
  const { data, isLoading } = useQuery({ queryKey: ["holidays", year], queryFn: async () => (await api.get("/holidays", { params: { year, mine: true } })).data });

  useEffect(() => { if (years?.length && !years.includes(year)) setYear(years[0]); }, [years]);

  return (
    <div>
      <PageHeader overline="Company" title="Holiday Calendar" icon={CalendarDays}
        subtitle="Approved holiday list published by HR. National holidays are off for everyone; restricted holidays are optional, up to 2 per calendar year, applied for under My Leave."
        actions={
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-32 bg-white" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{(years?.length ? years : [year]).map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        } />
      {isLoading ? <Loading /> : (data || []).length === 0 ? (
        <EmptyState icon={CalendarDays} title={`No holidays published for ${year}`} hint="HR has not published the holiday list for this year yet." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="holiday-list">
          {data.map((h) => {
            const d = new Date(h.date);
            return (
              <Card key={h.id} className="p-4 border-slate-200 shadow-none flex items-center gap-4">
                <div className="h-14 w-14 rounded-lg bg-navy/5 grid place-items-center shrink-0">
                  <span className="text-[10px] uppercase text-teal font-semibold">{MONTHS[d.getMonth()]}</span>
                  <span className="font-serif-display text-xl text-navy leading-none">{d.getDate()}</span>
                </div>
                <div>
                  <p className="font-medium text-navy">{h.name}</p>
                  <p className="text-xs text-slate-400">{h.type === "Restricted" ? "Restricted holiday" : h.type} · {d.toLocaleDateString(undefined, { weekday: "long" })}</p>
                  {h.location_names?.length > 0 && <span className="inline-block mt-1 rounded-full bg-red-100 text-red-700 text-[10px] font-semibold px-2 py-0.5">Site closure: {h.location_names.join(", ")}</span>}
                  {h.half_day && <span className="inline-block mt-1 ml-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold px-2 py-0.5">Half-day holiday: work {h.working_hours}h, no deduction for leaving early</span>}
                  {h.reason && <p className="text-xs text-slate-500 mt-1">{h.reason}</p>}
                  {h.type === "Restricted" && <span className="inline-block mt-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold px-2 py-0.5">Optional: choose any 2 a year</span>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
